import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal, Select } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { handleUploadErrors } from './utils';

import './UploadVehiclesModal.styles.scss';

type UploadVehiclesModalProps = {
  fleetOptions: OptionValue[];
  className?: string;
};

const UploadVehiclesModal: ForwardRefRenderFunction<
  OverlayRef,
  UploadVehiclesModalProps
> = (props, ref) => {
  const { fleetOptions, className } = props;
  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  const { t } = useTranslation();
  const { popError, popSuccess } = useContext(PopNotificationsContext);
  const uploadRef = useRef<HTMLInputElement>(null);
  const { isAdmin } = useContext(CurrentUserContext);
  const [selectedFleet, setSelectedFleet] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const classes = classNames('upload-vehicles-modal', className);

  useImperativeHandle(ref, () => modal, [modal]);

  const renderUploadErrors = useMemo(() => {
    if (errors?.length)
      return (
        <div className="upload-errors">
          {t('following-errors-occurred')}
          {errors.slice(0, 20).map((error) => (
            <span key={error}>{error}</span>
          ))}
          {errors.length > 20 && (
            <span>{t('and-number-more', { number: errors.length - 20 })}</span>
          )}
        </div>
      );
  }, [errors, t]);

  const handleUploadVehicles = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const files = event.target.files;
        await (isAdmin
          ? api.vehicle.uploadVehiclesAdmin(files[0], selectedFleet)
          : api.vehicle.uploadVehicles(files[0]));

        popSuccess(t('vehicles-upload-success'));
        modal?.close();
      } catch (error: any) {
        popError(t('vehicles-upload-error'));
        setErrors(handleUploadErrors(error.response?.data));
      } finally {
        event.target.value = '';
      }
    },
    [isAdmin, modal, popError, popSuccess, selectedFleet, t],
  );

  return (
    <Modal ref={modalRef} className={classes}>
      <p className="fm-card-title">{t('upload-vehicles')}</p>
      <p>{t('download-template-description')}</p>
      <a href="/assets/vehicles/Template.xlsx" download="Fleet template">
        {t('download-template')}
      </a>

      {isAdmin && (
        <Select
          options={fleetOptions}
          placeholder={t('selectFleet')}
          searchable
          value={selectedFleet}
          onChange={(opt: any) => {
            setSelectedFleet(opt.value);
          }}
        />
      )}

      {renderUploadErrors}

      <input
        type="file"
        id="upload-vehicles"
        name="upload-vehicles"
        ref={uploadRef}
        accept=".xlsx"
        hidden
        onChange={handleUploadVehicles}
      />
      <div className="actions">
        <Button aria-label="close" variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>
        <Button
          onClick={() => uploadRef.current?.click()}
          disabled={!selectedFleet}
        >
          {t('upload')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(UploadVehiclesModal);
